import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Plus, Trash2, Truck, ImagePlus, X, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPES = ["2 Wheeler", "3 Wheeler", "Mini Truck", "Truck", "Large Truck"];

const TYPE_BADGE = {
  "2 Wheeler":   "bg-sky-50 text-sky-700 border-sky-200",
  "3 Wheeler":   "bg-teal-50 text-teal-700 border-teal-200",
  "Mini Truck":  "bg-amber-50 text-amber-700 border-amber-200",
  "Truck":       "bg-orange-50 text-orange-700 border-orange-200",
  "Large Truck": "bg-rose-50 text-rose-700 border-rose-200",
};

const INPUT_CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";

const FormField = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block">{label}</label>
    {children}
  </div>
);

/* ── Image proxy helper ─────────────────── */
const VehicleImage = ({ path }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    axios.get(`${API}/vehicles/image/${path}`, { withCredentials: true, responseType: "blob" })
      .then(r => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path]);
  return src
    ? <img src={src} alt="vehicle" className="w-16 h-12 object-cover rounded-sm border border-[#E2E8F0]" />
    : <div className="w-16 h-12 bg-[#F1F5F9] rounded-sm border border-[#E2E8F0] animate-pulse" />;
};

export default function VehiclesPage() {
  const [vehicles, setVehicles]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({ vehicle_name: "", vehicle_number: "", vehicle_type: "", closed_vehicle: false, vehicle_capacity_kg: "", vehicle_volume_cbm: "" });
  const [imageFiles, setImageFiles] = useState([]);   // File objects
  const [previews, setPreviews]   = useState([]);     // blob URLs
  const fileInputRef              = useRef(null);

  const fetchVehicles = async () => {
    try {
      const { data } = await axios.get(`${API}/vehicles`, { withCredentials: true });
      setVehicles(data);
    } catch { toast.error("Failed to load vehicles."); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchVehicles(); }, []);

  /* Image selection */
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const total = imageFiles.length + files.length;
    if (total > 5) { toast.error("Maximum 5 images allowed."); return; }
    const newFiles    = [...imageFiles, ...files].slice(0, 5);
    const newPreviews = newFiles.map(f => URL.createObjectURL(f));
    previews.forEach(URL.revokeObjectURL);
    setImageFiles(newFiles);
    setPreviews(newPreviews);
    e.target.value = "";
  };

  const removeImage = (idx) => {
    URL.revokeObjectURL(previews[idx]);
    setImageFiles(f => f.filter((_, i) => i !== idx));
    setPreviews(p => p.filter((_, i) => i !== idx));
  };

  const resetForm = () => {
    setForm({ vehicle_name: "", vehicle_number: "", vehicle_type: "", closed_vehicle: false, vehicle_capacity_kg: "", vehicle_volume_cbm: "" });
    previews.forEach(URL.revokeObjectURL);
    setImageFiles([]);
    setPreviews([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_name || !form.vehicle_number || !form.vehicle_type) {
      toast.error("Vehicle Name, Number and Type are required.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("vehicle_name",       form.vehicle_name);
      fd.append("vehicle_number",     form.vehicle_number);
      fd.append("vehicle_type",       form.vehicle_type);
      fd.append("closed_vehicle",     String(form.closed_vehicle));
      if (form.vehicle_capacity_kg) fd.append("vehicle_capacity_kg", form.vehicle_capacity_kg);
      if (form.vehicle_volume_cbm)  fd.append("vehicle_volume_cbm",  form.vehicle_volume_cbm);
      imageFiles.forEach(f => fd.append("images", f));

      await axios.post(`${API}/vehicles`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Vehicle added successfully!");
      setShowAdd(false);
      resetForm();
      fetchVehicles();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add vehicle.");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await axios.delete(`${API}/vehicles/${id}`, { withCredentials: true });
      toast.success("Vehicle deleted.");
      setVehicles(prev => prev.filter(v => v.id !== id));
    } catch { toast.error("Failed to delete vehicle."); }
  };

  return (
    <div className="space-y-5" data-testid="vehicles-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">Vehicles</h2>
          <p className="text-xs text-[#475569] mt-0.5">{vehicles.length} vehicle{vehicles.length !== 1 ? "s" : ""} registered</p>
        </div>
        <Button
          data-testid="add-vehicle-btn"
          onClick={() => { resetForm(); setShowAdd(true); }}
          className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]"
        >
          <Plus size={14} className="mr-1.5" />Add Vehicle
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">Loading...</div>
        ) : vehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Truck size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No vehicles yet. <button onClick={() => { resetForm(); setShowAdd(true); }} className="text-[#1B2A6B] font-semibold underline">Add your first vehicle</button></p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vehicles-table">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                  {["#","Images","Vehicle Name","Vehicle No.","Type","Capacity (Kg)","Volume (m³)","Added On",""].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v, idx) => (
                  <tr key={v.id} data-testid={`vehicle-row-${idx}`} className="border-b border-[#E2E8F0] hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-4 py-3 text-[#94A3B8] text-xs font-mono">{idx + 1}</td>
                    <td className="px-4 py-3">
                      {v.image_paths?.length > 0 ? (
                        <div className="flex gap-1">
                          {v.image_paths.slice(0, 3).map((p, i) => <VehicleImage key={i} path={p} />)}
                          {v.image_paths.length > 3 && (
                            <div className="w-16 h-12 bg-[#F1F5F9] rounded-sm border border-[#E2E8F0] flex items-center justify-center text-[10px] text-[#94A3B8] font-bold">+{v.image_paths.length - 3}</div>
                          )}
                        </div>
                      ) : (
                        <div className="w-16 h-12 bg-[#F9FAFB] rounded-sm border border-[#E2E8F0] flex items-center justify-center">
                          <Truck size={16} className="text-[#CBD5E1]" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#0F172A]">{v.vehicle_name}</td>
                    <td className="px-4 py-3 font-mono text-[#1B2A6B] font-bold text-xs tracking-wider">{v.vehicle_number}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${TYPE_BADGE[v.vehicle_type] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                        {v.vehicle_type}{v.closed_vehicle ? " - Closed Vehicle" : ""}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#475569]">{v.vehicle_capacity_kg ? `${v.vehicle_capacity_kg} kg` : "—"}</td>
                    <td className="px-4 py-3 text-[#475569]">{v.vehicle_volume_cbm ? `${v.vehicle_volume_cbm} m³` : "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#475569] whitespace-nowrap">{format(new Date(v.created_at), "dd MMM yyyy")}</td>
                    <td className="px-4 py-3">
                      <button data-testid={`delete-vehicle-${idx}`} onClick={() => handleDelete(v.id)} className="text-[#CBD5E1] hover:text-red-500 transition-colors p-1" title="Delete"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Add Vehicle Dialog ── */}
      <Dialog open={showAdd} onOpenChange={o => { if (!o) resetForm(); setShowAdd(o); }}>
        <DialogContent className="max-w-2xl rounded-none p-0 gap-0 max-h-[90vh] overflow-y-auto">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700] sticky top-0 z-10">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Add New Vehicle</DialogTitle>
            <p className="text-white/60 text-xs mt-0.5">Fill in the vehicle details below</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">

              <FormField label="Vehicle Name *">
                <Input name="vehicle_name" value={form.vehicle_name} onChange={e => setForm(f => ({...f, vehicle_name: e.target.value}))} placeholder="e.g. Ashok Leyland" data-testid="vehicle-name-input" className={INPUT_CLS} />
              </FormField>

              <FormField label="Vehicle Number *">
                <Input name="vehicle_number" value={form.vehicle_number} onChange={e => setForm(f => ({...f, vehicle_number: e.target.value.toUpperCase()}))} placeholder="e.g. MH01AB1234" data-testid="vehicle-number-input" className={`${INPUT_CLS} uppercase font-mono tracking-wider`} />
              </FormField>

              <div className="sm:col-span-2">
                <FormField label="Vehicle Type *">
                  <Select value={form.vehicle_type} onValueChange={v => setForm(f => ({...f, vehicle_type: v}))}>
                    <SelectTrigger data-testid="vehicle-type-select" className="rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm">
                      <SelectValue placeholder="Select vehicle type" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {VEHICLE_TYPES.map(t => <SelectItem key={t} value={t} className="rounded-none">{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>

              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer w-fit">
                  <input
                    type="checkbox"
                    checked={form.closed_vehicle}
                    onChange={e => setForm(f => ({ ...f, closed_vehicle: e.target.checked }))}
                    className="accent-[#1B2A6B] w-4 h-4"
                    data-testid="closed-vehicle-checkbox"
                  />
                  <span className="text-xs font-bold text-[#0F172A] uppercase tracking-widest">Closed Vehicle</span>
                </label>
              </div>

              <FormField label="Capacity (Kg) — Optional">
                <Input type="number" min="0" value={form.vehicle_capacity_kg} onChange={e => setForm(f => ({...f, vehicle_capacity_kg: e.target.value}))} placeholder="e.g. 1000" data-testid="vehicle-capacity-input" className={INPUT_CLS} />
              </FormField>

              <FormField label="Volume (Cubic Meters) — Optional">
                <Input type="number" min="0" step="0.01" value={form.vehicle_volume_cbm} onChange={e => setForm(f => ({...f, vehicle_volume_cbm: e.target.value}))} placeholder="e.g. 12.5" data-testid="vehicle-volume-input" className={INPUT_CLS} />
              </FormField>

              {/* Image upload — full width */}
              <div className="sm:col-span-2">
                <FormField label={`Vehicle Images — Optional (Max 5, ${imageFiles.length}/5 selected)`}>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-[#E2E8F0] hover:border-[#1B2A6B] transition-colors p-5 text-center cursor-pointer"
                    data-testid="image-upload-zone"
                  >
                    <Upload size={22} className="text-[#94A3B8] mx-auto mb-2" />
                    <p className="text-sm text-[#475569]">Click to upload images</p>
                    <p className="text-[11px] text-[#94A3B8] mt-0.5">JPG, PNG, WEBP — max 5 images</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageSelect}
                    data-testid="image-file-input"
                  />

                  {/* Previews */}
                  {previews.length > 0 && (
                    <div className="flex gap-3 mt-3 flex-wrap">
                      {previews.map((src, i) => (
                        <div key={i} className="relative group">
                          <img src={src} alt={`preview-${i}`} className="w-20 h-16 object-cover border border-[#E2E8F0]" />
                          <button
                            type="button"
                            onClick={() => removeImage(i)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                      {imageFiles.length < 5 && (
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="w-20 h-16 border-2 border-dashed border-[#E2E8F0] hover:border-[#1B2A6B] flex items-center justify-center transition-colors">
                          <ImagePlus size={18} className="text-[#94A3B8]" />
                        </button>
                      )}
                    </div>
                  )}
                </FormField>
              </div>
            </div>

            <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
              <button type="button" onClick={() => { resetForm(); setShowAdd(false); }} data-testid="vehicle-cancel-btn" className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">Cancel</button>
              <Button type="submit" data-testid="vehicle-save-btn" disabled={saving} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
                {saving ? "Saving..." : "Save Vehicle"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
